import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import "./ApplicationForm.css";
import { useLuckyOrange } from '../../hooks/useLuckyOrange';

interface ApplicationFormInputs {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  birthDate: string;
  streetAddress: string;
  addressLine2?: string;
  city: string;
  stateOrProvince: string;
  postalCode: string;
  jobAppliedFor: string;
  affiliatedUnion?: string;
  referenceName1?: string;
  referenceTitle1?: string;
  referenceRelationship1?: string;
  referenceEmail1?: string;
  referencePhoneNumber1?: string;
  referenceName2?: string;
  referenceTitle2?: string;
  referenceRelationship2?: string;
  referenceEmail2?: string;
  referencePhoneNumber2?: string;
  previousWorkPlace1?: string;
  previousJobTitle1?: string;
  previousJobStartDate1?: string;
  previousJobEndDate1?: string;
  previousJobPhoneNumber1?: string;
  previousWorkPlace2?: string;
  previousJobTitle2?: string;
  previousJobStartDate2?: string;
  previousJobEndDate2?: string;
  previousJobPhoneNumber2?: string;
  resume?: FileList;
}

const ApplicationForm: React.FC = () => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    trigger,
    watch,
    clearErrors,
  } = useForm<ApplicationFormInputs>({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      birthDate: '',
      streetAddress: '',
      addressLine2: '',
      city: '',
      stateOrProvince: '',
      postalCode: '',
      jobAppliedFor: '',
      affiliatedUnion: '',
      referenceName1: '',
      referenceTitle1: '',
      referenceRelationship1: '',
      referenceEmail1: '',
      referencePhoneNumber1: '',
      referenceName2: '',
      referenceTitle2: '',
      referenceRelationship2: '',
      referenceEmail2: '',
      referencePhoneNumber2: '',
      previousWorkPlace1: '',
      previousJobTitle1: '',
      previousJobStartDate1: '',
      previousJobEndDate1: '',
      previousJobPhoneNumber1: '',
      previousWorkPlace2: '',
      previousJobTitle2: '',
      previousJobStartDate2: '',
      previousJobEndDate2: '',
      previousJobPhoneNumber2: '',
    }
  });
  
  const [submissionStatus, setSubmissionStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [apiError, setApiError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [formKey, setFormKey] = useState(0); // Add form key for forcing re-render
  
  // ── Lucky Orange analytics ──────────────────────────────────────────────
  // `trackMilestone` and `identifyApplication` are both queue-safe: calling
  // them before the LO script loads simply enqueues the command for replay.
  const { trackMilestone, identifyApplication } = useLuckyOrange();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

  const usStates = [
    { value: "", label: "Select State/Territory" },
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "DC", label: "District of Columbia" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" },
    // US Territories
    { value: "AS", label: "American Samoa" },
    { value: "GU", label: "Guam" },
    { value: "MP", label: "Northern Mariana Islands" },
    { value: "PR", label: "Puerto Rico" },
    { value: "VI", label: "U.S. Virgin Islands" },
    // Other
    { value: "OTHER", label: "Other (Non-US)" }
  ];

  const steps = [
    { id: 1, title: "Personal Information", icon: "👤" },
    { id: 2, title: "Address Details", icon: "📍" },
    { id: 3, title: "Job Application", icon: "💼" },
    { id: 4, title: "References", icon: "👥" },
    { id: 5, title: "Work History", icon: "📝" },
  ];

  const watchedFields = watch();

  // ── Milestone: Application Started ────────────────────────────────────────
  // Fire once when the form mounts. The empty dependency array ensures this
  // runs exactly once regardless of re-renders or step navigation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    trackMilestone('Application Started', { formName: 'JobApplication' });
  }, []); // intentionally empty — fire once on form entry

  // Clear errors when changing steps to prevent cross-step validation issues
  useEffect(() => {
    clearErrors();
  }, [currentStep, clearErrors]);

  // Reset form completely when formKey changes
  useEffect(() => {
    if (formKey > 0) {
      reset({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        birthDate: '',
        streetAddress: '',
        addressLine2: '',
        city: '',
        stateOrProvince: '',
        postalCode: '',
        jobAppliedFor: '',
        affiliatedUnion: '',
        referenceName1: '',
        referenceTitle1: '',
        referenceRelationship1: '',
        referenceEmail1: '',
        referencePhoneNumber1: '',
        referenceName2: '',
        referenceTitle2: '',
        referenceRelationship2: '',
        referenceEmail2: '',
        referencePhoneNumber2: '',
        previousWorkPlace1: '',
        previousJobTitle1: '',
        previousJobStartDate1: '',
        previousJobEndDate1: '',
        previousJobPhoneNumber1: '',
        previousWorkPlace2: '',
        previousJobTitle2: '',
        previousJobStartDate2: '',
        previousJobEndDate2: '',
        previousJobPhoneNumber2: '',
      });
    }
  }, [formKey, reset]);

  const validateStep = async (step: number): Promise<boolean> => {
    let fieldsToValidate: (keyof ApplicationFormInputs)[] = [];
    
    switch (step) {
      case 1:
        fieldsToValidate = ["firstName", "lastName", "email", "phoneNumber", "birthDate"];
        break;
      case 2:
        fieldsToValidate = ["streetAddress", "city", "stateOrProvince", "postalCode"];
        break;
      case 3:
        fieldsToValidate = ["jobAppliedFor", "resume"];
        break;
      case 4:
      case 5:
        return true; // Optional sections
      default:
        return true;
    }

    const result = await trigger(fieldsToValidate);
    return result;
  };

  const nextStep = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const isValid = await validateStep(currentStep);
    if (isValid) {
      // MILESTONE: Step Completed — metadata is non-PII (step index + label)
      trackMilestone('Step Completed', {
        step: currentStep,
        stepName: steps[currentStep - 1].title,
      });
      setCompletedSteps(prev => [...prev.filter(s => s !== currentStep), currentStep]);
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    } else {
      // MILESTONE: Validation Error — signals where applicants get stuck
      trackMilestone('Validation Error', {
        step: currentStep,
        stepName: steps[currentStep - 1].title,
      });
    }
  };

  const prevStep = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const goToStep = async (step: number) => {
    if (step <= currentStep || completedSteps.includes(step - 1) || step === 1) {
      clearErrors(); // Clear any previous errors when switching steps
      setCurrentStep(step);
    }
  };

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    // Prevent form submission when Enter is pressed, except on the final step
    if (e.key === 'Enter') {
      const target = e.target as HTMLInputElement | HTMLButtonElement;
      // Only prevent if not on a submit button and not on final step
      if (target.type !== 'submit' && currentStep < steps.length) {
        e.preventDefault();
        return;
      }
      // If on final step and Enter is pressed on input, allow default behavior
      if (currentStep === steps.length && target.type !== 'submit') {
        e.preventDefault();
        return;
      }
    }
  };

  const calculateProgress = () => {
    const requiredFields = [
      watchedFields.firstName, watchedFields.lastName, watchedFields.email, 
      watchedFields.phoneNumber, watchedFields.birthDate, watchedFields.streetAddress,
      watchedFields.city, watchedFields.stateOrProvince, watchedFields.postalCode,
      watchedFields.jobAppliedFor
    ];
    
    const filledFields = requiredFields.filter(field => field && field.toString().trim() !== '').length;
    return Math.round((filledFields / requiredFields.length) * 100);
  };

  const onSubmit = async (data: ApplicationFormInputs) => {
    console.log("Form submitted at step:", currentStep);
    
    // Only allow submission on the final step
    if (currentStep !== steps.length) {
      console.log("Preventing submission - not on final step");
      return;
    }
    
    setSubmissionStatus("submitting");
    setApiError(null);
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'resume' && value && value.length > 0) {
        formData.append(key, value[0]);
      } else if (value) {
        formData.append(key, value);
      }
    });

    try {
      const response = await fetch(`${API_BASE_URL}/JobApplication/upload`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        // Attempt to read the response body to extract the Application ID.
        // If the API returns the created record we use its ID to correlate
        // the Lucky Orange session recording with our database — no PII passed.
        let createdId: string | undefined;
        try {
          const responseData = await response.json();
          createdId = responseData?.id ?? responseData?.applicationId;
        } catch (_) {
          // Response body is empty or not JSON — identification will be skipped
        }
        if (createdId) {
          // IDENTIFY: links this session recording to the application record
          identifyApplication(String(createdId));
        }

        // MILESTONE: Application Submitted
        // Only the position title is included — no name, email, or other PII.
        trackMilestone('Application Submitted', {
          jobAppliedFor: data.jobAppliedFor,
        });

        setSubmissionStatus("success");
        reset();
        setCurrentStep(1);
        setCompletedSteps([]);
        setFormKey(prev => prev + 1); // Force form re-render
        // Redirect to thank you page
        navigate('/thank-you');
      } else {
        setSubmissionStatus("error");

        if (response.status === 400) {
          const errorData = await response.json();
          if (errorData && errorData.errors) {
            let hasFieldError = false;

            Object.keys(errorData.errors).forEach((key) => {
              const messages = errorData.errors[key];
              const fieldName = key.charAt(0).toLowerCase() + key.slice(1);

              if (key && Object.keys(data).includes(fieldName)) {
                setError(fieldName as keyof ApplicationFormInputs, {
                  type: "server",
                  message: messages[0],
                });
                hasFieldError = true;
              } else if (key === "") {
                setApiError(messages[0]);
              }
            });

            if (!hasFieldError && !errorData.errors[""]) {
              setApiError(
                errorData.title || "An unknown validation error occurred."
              );
            }
          } else {
            setApiError(
              errorData.title || "An unknown validation error occurred."
            );
          }
        } else {
          const errorText = await response.text();
          setApiError(`Submission failed: ${errorText || response.statusText}`);
          console.error("Submission failed:", errorText || response.statusText);
        }
      }
    } catch (error) {
      setSubmissionStatus("error");
      setApiError(
        "An error occurred during submission. Please check your network connection."
      );
      console.error("Submission error:", error);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <div className="step-header">
              <h3>👤 Personal Information</h3>
              <p>Let's start with your basic information</p>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="firstName" className="form-label">
                  First Name <span className="required">*</span>
                </label>
                <input 
                  id="firstName" 
                  type="text" 
                  {...register("firstName", { 
                    required: "First Name is required", 
                    maxLength: { value: 50, message: "First Name cannot exceed 50 characters" } 
                  })} 
                  className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                  placeholder="Enter your first name"
                  autoComplete="off"
                />
                {errors.firstName && <div className="invalid-feedback">{errors.firstName.message}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="lastName" className="form-label">
                  Last Name <span className="required">*</span>
                </label>
                <input 
                  id="lastName" 
                  type="text" 
                  {...register("lastName", { 
                    required: "Last Name is required", 
                    maxLength: { value: 50, message: "Last Name cannot exceed 50 characters" } 
                  })} 
                  className={`form-control ${errors.lastName ? "is-invalid" : ""}`}
                  placeholder="Enter your last name"
                  autoComplete="off"
                />
                {errors.lastName && <div className="invalid-feedback">{errors.lastName.message}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address <span className="required">*</span>
                </label>
                <input 
                  id="email" 
                  type="email" 
                  {...register("email", { 
                    required: "Email is required", 
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email format" }, 
                    maxLength: { value: 100, message: "Email cannot exceed 100 characters" } 
                  })} 
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  placeholder="your.email@example.com"
                  autoComplete="off"
                />
                {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="phoneNumber" className="form-label">
                  Phone Number <span className="required">*</span>
                </label>
                <input 
                  id="phoneNumber" 
                  type="tel" 
                  {...register("phoneNumber", { 
                    required: "Phone Number is required", 
                    maxLength: { value: 20, message: "Phone Number cannot exceed 20 characters" } 
                  })} 
                  className={`form-control ${errors.phoneNumber ? "is-invalid" : ""}`}
                  placeholder="(555) 123-4567"
                  autoComplete="off"
                />
                {errors.phoneNumber && <div className="invalid-feedback">{errors.phoneNumber.message}</div>}
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="birthDate" className="form-label">
                  Date of Birth <span className="required">*</span>
                </label>
                <input 
                  id="birthDate" 
                  type="date" 
                  max={new Date().toISOString().split('T')[0]} 
                  {...register("birthDate", { required: "Birth Date is required" })} 
                  className={`form-control ${errors.birthDate ? "is-invalid" : ""}`}
                  // data-lo-mask: prevents Lucky Orange from recording this
                  // field's value in session recordings. Required for all DOB fields.
                  data-lo-mask
                />
                {errors.birthDate && <div className="invalid-feedback">{errors.birthDate.message}</div>}
              </div>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="step-content">
            <div className="step-header">
              <h3>📍 Address Information</h3>
              <p>Where can we reach you?</p>
            </div>
            <div className="form-grid">
              <div className="form-group full-width">
                <label htmlFor="streetAddress" className="form-label">
                  Street Address <span className="required">*</span>
                </label>
                <input 
                  id="streetAddress" 
                  type="text" 
                  {...register("streetAddress", { 
                    required: "Street Address is required", 
                    maxLength: { value: 150, message: "Street Address cannot exceed 150 characters" } 
                  })} 
                  className={`form-control ${errors.streetAddress ? "is-invalid" : ""}`}
                  placeholder="123 Main Street"
                />
                {errors.streetAddress && <div className="invalid-feedback">{errors.streetAddress.message}</div>}
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="addressLine2" className="form-label">
                  Address Line 2 <span className="optional">(Optional)</span>
                </label>
                <input 
                  id="addressLine2" 
                  type="text" 
                  {...register("addressLine2", { 
                    maxLength: { value: 150, message: "Address Line 2 cannot exceed 150 characters" } 
                  })} 
                  className="form-control"
                  placeholder="Apartment, suite, unit, building, floor, etc."
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="city" className="form-label">
                  City <span className="required">*</span>
                </label>
                <input 
                  id="city" 
                  type="text" 
                  {...register("city", { 
                    required: "City is required", 
                    maxLength: { value: 100, message: "City cannot exceed 100 characters" } 
                  })} 
                  className={`form-control ${errors.city ? "is-invalid" : ""}`}
                  placeholder="Enter city"
                />
                {errors.city && <div className="invalid-feedback">{errors.city.message}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="stateOrProvince" className="form-label">
                  State/Province <span className="required">*</span>
                </label>
                <select 
                  id="stateOrProvince" 
                  {...register("stateOrProvince", { 
                    required: "State/Province is required"
                  })} 
                  className={`form-control ${errors.stateOrProvince ? "is-invalid" : ""}`}
                >
                  {usStates.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
                {errors.stateOrProvince && <div className="invalid-feedback">{errors.stateOrProvince.message}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="postalCode" className="form-label">
                  Postal Code <span className="required">*</span>
                </label>
                <input 
                  id="postalCode" 
                  type="text" 
                  {...register("postalCode", { 
                    required: "Postal Code is required", 
                    maxLength: { value: 20, message: "Postal Code cannot exceed 20 characters" } 
                  })} 
                  className={`form-control ${errors.postalCode ? "is-invalid" : ""}`}
                  placeholder="12345"
                />
                {errors.postalCode && <div className="invalid-feedback">{errors.postalCode.message}</div>}
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="step-content">
            <div className="step-header">
              <h3>💼 Job Application Details</h3>
              <p>Tell us about the position you're interested in</p>
            </div>
            <div className="form-grid">
              <div className="form-group full-width">
                <label htmlFor="jobAppliedFor" className="form-label">
                  Position Applied For <span className="required">*</span>
                </label>
                <input 
                  id="jobAppliedFor" 
                  type="text" 
                  {...register("jobAppliedFor", { 
                    required: "Job Applied For is required", 
                    maxLength: { value: 100, message: "Job Applied For cannot exceed 100 characters" } 
                  })} 
                  className={`form-control ${errors.jobAppliedFor ? "is-invalid" : ""}`}
                  placeholder="Software Developer, Project Manager, etc."
                />
                {errors.jobAppliedFor && <div className="invalid-feedback">{errors.jobAppliedFor.message}</div>}
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="affiliatedUnion" className="form-label">
                  Affiliated Union <span className="optional">(If applicable)</span>
                </label>
                <input 
                  id="affiliatedUnion" 
                  type="text" 
                  {...register("affiliatedUnion", { 
                    maxLength: { value: 100, message: "Affiliated Union cannot exceed 100 characters" } 
                  })} 
                  className="form-control"
                  placeholder="Enter union name if applicable"
                />
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="resume" className="form-label">
                  Resume <span className="optional">(PDF, DOC, DOCX, JPG, PNG, HEIC)</span>
                </label>
                <div className="file-upload-wrapper">
                  <input 
                    id="resume" 
                    type="file" 
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic" 
                    {...register("resume", {
                      validate: (files) => {
                        if (!files || files.length === 0) return true;
                        const allowed = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png", ".heic"];
                        const ext = files[0].name.slice(files[0].name.lastIndexOf(".")).toLowerCase();
                        return allowed.includes(ext) || "Unsupported file type. Please upload a PDF, DOC, DOCX, JPG, PNG, or HEIC file.";
                      }
                    })}
                    className={`form-control ${errors.resume ? "is-invalid" : ""}`}
                  />
                  <div className="file-upload-hint">
                    <small>Supported formats: PDF, DOC, DOCX, JPG, PNG, HEIC (Max 10MB)</small>
                  </div>
                </div>
                {errors.resume && <div className="invalid-feedback">{errors.resume.message}</div>}
              </div>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="step-content">
            <div className="step-header">
              <h3>👥 Professional References</h3>
              <p>Provide references who can speak to your professional experience</p>
              <div className="optional-badge">Optional Section</div>
            </div>
            <div className="references-section">
              <div className="reference-card">
                <h4>Reference 1</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="referenceName1" className="form-label">Full Name</label>
                    <input 
                      id="referenceName1" 
                      type="text" 
                      {...register("referenceName1", { 
                        maxLength: { value: 100, message: "Reference Name cannot exceed 100 characters" } 
                      })} 
                      className={`form-control ${errors.referenceName1 ? "is-invalid" : ""}`}
                      placeholder="John Smith"
                    />
                    {errors.referenceName1 && <div className="invalid-feedback">{errors.referenceName1.message}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="referenceTitle1" className="form-label">Job Title</label>
                    <input 
                      id="referenceTitle1" 
                      type="text" 
                      {...register("referenceTitle1", { 
                        maxLength: { value: 100, message: "Reference Title cannot exceed 100 characters" } 
                      })} 
                      className={`form-control ${errors.referenceTitle1 ? "is-invalid" : ""}`}
                      placeholder="Senior Manager"
                    />
                    {errors.referenceTitle1 && <div className="invalid-feedback">{errors.referenceTitle1.message}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="referenceRelationship1" className="form-label">Relationship</label>
                    <input 
                      id="referenceRelationship1" 
                      type="text" 
                      {...register("referenceRelationship1", { 
                        maxLength: { value: 100, message: "Reference Relationship cannot exceed 100 characters" } 
                      })} 
                      className={`form-control ${errors.referenceRelationship1 ? "is-invalid" : ""}`}
                      placeholder="Former Supervisor"
                    />
                    {errors.referenceRelationship1 && <div className="invalid-feedback">{errors.referenceRelationship1.message}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="referenceEmail1" className="form-label">Email</label>
                    <input 
                      id="referenceEmail1" 
                      type="email" 
                      {...register("referenceEmail1", { 
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email format" }, 
                        maxLength: { value: 100, message: "Reference Email cannot exceed 100 characters" } 
                      })} 
                      className={`form-control ${errors.referenceEmail1 ? "is-invalid" : ""}`}
                      placeholder="john.smith@company.com"
                    />
                    {errors.referenceEmail1 && <div className="invalid-feedback">{errors.referenceEmail1.message}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="referencePhoneNumber1" className="form-label">Phone Number</label>
                    <input 
                      id="referencePhoneNumber1" 
                      type="tel" 
                      {...register("referencePhoneNumber1", { 
                        maxLength: { value: 20, message: "Reference Phone Number cannot exceed 20 characters" } 
                      })} 
                      className={`form-control ${errors.referencePhoneNumber1 ? "is-invalid" : ""}`}
                      placeholder="(555) 123-4567"
                    />
                    {errors.referencePhoneNumber1 && <div className="invalid-feedback">{errors.referencePhoneNumber1.message}</div>}
                  </div>
                </div>
              </div>
              
              <div className="reference-card">
                <h4>Reference 2</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="referenceName2" className="form-label">Full Name</label>
                    <input 
                      id="referenceName2" 
                      type="text" 
                      {...register("referenceName2", { 
                        maxLength: { value: 100, message: "Reference Name cannot exceed 100 characters" } 
                      })} 
                      className={`form-control ${errors.referenceName2 ? "is-invalid" : ""}`}
                      placeholder="Jane Doe"
                    />
                    {errors.referenceName2 && <div className="invalid-feedback">{errors.referenceName2.message}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="referenceTitle2" className="form-label">Job Title</label>
                    <input 
                      id="referenceTitle2" 
                      type="text" 
                      {...register("referenceTitle2", { 
                        maxLength: { value: 100, message: "Reference Title cannot exceed 100 characters" } 
                      })} 
                      className={`form-control ${errors.referenceTitle2 ? "is-invalid" : ""}`}
                      placeholder="Director"
                    />
                    {errors.referenceTitle2 && <div className="invalid-feedback">{errors.referenceTitle2.message}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="referenceRelationship2" className="form-label">Relationship</label>
                    <input 
                      id="referenceRelationship2" 
                      type="text" 
                      {...register("referenceRelationship2", { 
                        maxLength: { value: 100, message: "Reference Relationship cannot exceed 100 characters" } 
                      })} 
                      className={`form-control ${errors.referenceRelationship2 ? "is-invalid" : ""}`}
                      placeholder="Former Colleague"
                    />
                    {errors.referenceRelationship2 && <div className="invalid-feedback">{errors.referenceRelationship2.message}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="referenceEmail2" className="form-label">Email</label>
                    <input 
                      id="referenceEmail2" 
                      type="email" 
                      {...register("referenceEmail2", { 
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email format" }, 
                        maxLength: { value: 100, message: "Reference Email cannot exceed 100 characters" } 
                      })} 
                      className={`form-control ${errors.referenceEmail2 ? "is-invalid" : ""}`}
                      placeholder="jane.doe@company.com"
                    />
                    {errors.referenceEmail2 && <div className="invalid-feedback">{errors.referenceEmail2.message}</div>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="referencePhoneNumber2" className="form-label">Phone Number</label>
                    <input 
                      id="referencePhoneNumber2" 
                      type="tel" 
                      {...register("referencePhoneNumber2", { 
                        maxLength: { value: 20, message: "Reference Phone Number cannot exceed 20 characters" } 
                      })} 
                      className={`form-control ${errors.referencePhoneNumber2 ? "is-invalid" : ""}`}
                      placeholder="(555) 987-6543"
                    />
                    {errors.referencePhoneNumber2 && <div className="invalid-feedback">{errors.referencePhoneNumber2.message}</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 5:
        return (
          <div className="step-content">
            <div className="step-header">
              <h3>📝 Work History</h3>
              <p>Share your previous work experience</p>
              <div className="optional-badge">Optional Section</div>
            </div>
            <div className="work-history-section">
              <div className="work-history-card">
                <h4>Previous Position 1</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="previousWorkPlace1" className="form-label">Company Name</label>
                    <input 
                      id="previousWorkPlace1" 
                      type="text" 
                      {...register("previousWorkPlace1", { 
                        maxLength: { value: 150, message: "Previous Work Place cannot exceed 150 characters" } 
                      })} 
                      className="form-control"
                      placeholder="ABC Company Inc."
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="previousJobTitle1" className="form-label">Job Title</label>
                    <input 
                      id="previousJobTitle1" 
                      type="text" 
                      {...register("previousJobTitle1", { 
                        maxLength: { value: 100, message: "Previous Job Title cannot exceed 100 characters" } 
                      })} 
                      className="form-control"
                      placeholder="Software Developer"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="previousJobStartDate1" className="form-label">Start Date</label>
                    <input 
                      id="previousJobStartDate1" 
                      type="date" 
                      max={new Date().toISOString().split('T')[0]} 
                      {...register("previousJobStartDate1")} 
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="previousJobEndDate1" className="form-label">End Date</label>
                    <input 
                      id="previousJobEndDate1" 
                      type="date" 
                      max={new Date().toISOString().split('T')[0]} 
                      {...register("previousJobEndDate1")} 
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-group full-width">
                    <label htmlFor="previousJobPhoneNumber1" className="form-label">Company Phone</label>
                    <input 
                      id="previousJobPhoneNumber1" 
                      type="tel" 
                      {...register("previousJobPhoneNumber1", { 
                        maxLength: { value: 20, message: "Previous Job Phone Number cannot exceed 20 characters" } 
                      })} 
                      className="form-control"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>
              
              <div className="work-history-card">
                <h4>Previous Position 2</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="previousWorkPlace2" className="form-label">Company Name</label>
                    <input 
                      id="previousWorkPlace2" 
                      type="text" 
                      {...register("previousWorkPlace2", { 
                        maxLength: { value: 150, message: "Previous Work Place cannot exceed 150 characters" } 
                      })} 
                      className="form-control"
                      placeholder="XYZ Corporation"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="previousJobTitle2" className="form-label">Job Title</label>
                    <input 
                      id="previousJobTitle2" 
                      type="text" 
                      {...register("previousJobTitle2", { 
                        maxLength: { value: 100, message: "Previous Job Title cannot exceed 100 characters" } 
                      })} 
                      className="form-control"
                      placeholder="Junior Developer"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="previousJobStartDate2" className="form-label">Start Date</label>
                    <input 
                      id="previousJobStartDate2" 
                      type="date" 
                      max={new Date().toISOString().split('T')[0]} 
                      {...register("previousJobStartDate2")} 
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="previousJobEndDate2" className="form-label">End Date</label>
                    <input 
                      id="previousJobEndDate2" 
                      type="date" 
                      max={new Date().toISOString().split('T')[0]} 
                      {...register("previousJobEndDate2")} 
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-group full-width">
                    <label htmlFor="previousJobPhoneNumber2" className="form-label">Company Phone</label>
                    <input 
                      id="previousJobPhoneNumber2" 
                      type="tel" 
                      {...register("previousJobPhoneNumber2", { 
                        maxLength: { value: 20, message: "Previous Job Phone Number cannot exceed 20 characters" } 
                      })} 
                      className="form-control"
                      placeholder="(555) 987-6543"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="application-form-container">
      <div className="form-header">
        <h1>Job Application</h1>
        <p>Take your next career step with us</p>
        
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${calculateProgress()}%` }}
            ></div>
          </div>
          <span className="progress-text">{calculateProgress()}% Complete</span>
        </div>
      </div>

      <div className="step-indicator">
        {steps.map((step) => (
          <div 
            key={step.id}
            className={`step ${currentStep === step.id ? 'active' : ''} ${
              completedSteps.includes(step.id) ? 'completed' : ''
            } ${step.id < currentStep || completedSteps.includes(step.id - 1) || step.id === 1 ? 'clickable' : ''}`}
            onClick={() => goToStep(step.id)}
          >
            <div className="step-icon">
              {completedSteps.includes(step.id) ? '✓' : step.icon}
            </div>
            <div className="step-title">{step.title}</div>
          </div>
        ))}
      </div>

      <form 
        key={formKey}
        onSubmit={handleSubmit(onSubmit)} 
        onKeyDown={handleFormKeyDown}
        className="application-form" 
        noValidate
        autoComplete="off"
      >
        {renderStepContent()}

        <div className="form-navigation">
          {currentStep > 1 && (
            <button 
              type="button" 
              onClick={prevStep}
              className="btn btn-secondary"
            >
              ← Previous
            </button>
          )}
          
          <div className="nav-spacer"></div>
          
          {currentStep < steps.length ? (
            <button 
              type="button" 
              onClick={nextStep}
              className="btn btn-primary"
            >
              Next →
            </button>
          ) : (
            <button
              type="submit"
              className="btn btn-success"
              disabled={submissionStatus === "submitting"}
            >
              {submissionStatus === "submitting" && (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              )}
              {submissionStatus === "submitting" ? "Submitting..." : "Submit Application"}
            </button>
          )}
        </div>

        {submissionStatus === "success" && (
          <div className="alert alert-success">
            <div className="success-icon">🎉</div>
            <div>
              <h4>Application Submitted Successfully!</h4>
              <p>Thank you for your application. We'll review it and get back to you soon.</p>
            </div>
          </div>
        )}
        
        {apiError && (
          <div className="alert alert-danger">
            <div className="error-icon">⚠️</div>
            <div>
              <h4>Submission Error</h4>
              <p>{apiError}</p>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ApplicationForm;
